import { useEffect, useMemo, useState } from 'react';
import { api, type ProfitInstRow } from '../lib/api';
import { ErrorBox, money } from '../components/DataTable';
import { useProfitMonths } from './profitMonths';
import { CalcNote } from '../components/CalcNote';

/**
 * 경영 분석 › 할부금 — 수익TOOL 「🚍할부금분석」·「🚍실할부금정보」 대체(10-24). 서버 `listProfitInst`(`functions/src/profitOps.ts` INST_SQL).
 * 차량마다 그 달 기사 할부금 공제 ↔ 다음 달 회사 실납부(공제 정산월 = 납부월 − 1).
 * 묶음 = 시트 세 표(공제만 · 회사가 더 냄 · 회사가 공제 없이 냄) + 같음 · 공제가 더 큼.
 */

type Pick = 'more' | 'payOnly' | 'direct' | 'dedOnly' | 'less' | 'same' | 'all';
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const n = (v: string | null) => (v === null ? null : Number(v));
function pickOf(r: ProfitInstRow): Exclude<Pick, 'all'> {
  const d = n(r.ded), p = n(r.pay);
  if (r.direct && !d) return 'direct';
  if (d !== null && p !== null) return d === p ? 'same' : p > d ? 'more' : 'less';
  return d !== null ? 'dedOnly' : 'payOnly';
}
const LABEL: Record<Pick, string> = {
  more: '회사가 더 냄', payOnly: '회사가 공제 없이 냄', direct: '직영(회사 부담)', dedOnly: '공제만 있음', less: '공제가 더 큼', same: '같음', all: '전체',
};
const PICKS = ['more', 'payOnly', 'direct', 'dedOnly', 'less', 'same', 'all'] as const;

export default function ProfitInst() {
  const [error, setError] = useState<string | null>(null);
  const [months, mon, setMon] = useProfitMonths(setError);
  const [pick, setPick] = useState<Pick>('more');
  const [q, setQ] = useState('');
  const [data, setData] = useState<{ payMon: string; rows: ProfitInstRow[] } | null>(null);

  useEffect(() => {
    if (!mon) return;
    let cancelled = false;
    setData(null); setError(null);
    api.listProfitInst({ mon }).then((r) => { if (!cancelled) setData(r); }).catch((e) => { if (!cancelled) setError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [mon]);

  const stat = useMemo(() => {
    const s = Object.fromEntries(PICKS.map((k) => [k, { n: 0, amt: 0 }])) as Record<Pick, { n: number; amt: number }>;
    for (const r of data?.rows ?? []) {
      const k = pickOf(r), gap = (n(r.pay) ?? 0) - (n(r.ded) ?? 0);
      s[k].n++; s[k].amt += gap; s.all.n++; s.all.amt += gap;
    }
    return s;
  }, [data]);
  const shown = useMemo(() => {
    const w = q.trim().replace(/\s/g, '');
    return (data?.rows ?? []).filter((r) => (pick === 'all' || pickOf(r) === pick)
      && (!w || r.car_no.includes(w) || (r.owner_name ?? '').includes(w) || (r.holder_name ?? '').includes(w) || (r.company ?? '').includes(w)))
      .sort((a, b) => Math.abs((n(b.pay) ?? 0) - (n(b.ded) ?? 0)) - Math.abs((n(a.pay) ?? 0) - (n(a.ded) ?? 0)));
  }, [data, pick, q]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select className="field w-auto" value={mon} onChange={(e) => setMon(e.target.value)} aria-label="정산월">
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input className="field w-48" placeholder="차량·차주·명의·캐피탈" value={q} onChange={(e) => setQ(e.target.value)} />
        <CalcNote>기사 공제 = 그 달 명세의 할부금 공제 · 회사 납부 = 할부금 › 회사 실납부의 다음 달 회차 금액(공제 정산월 = 납부월 − 1).
        「회사할부」·「회사대출」 실납부는 회사가 차주에게 빌려준 돈(회사 통장에서 나가지 않음)이라 회사 납부에 넣지 않습니다. 기사 공제 중 그 계약 몫은 「그중 회사할부 공제」 칸에 참고로 보입니다.
        계약이 2 이상인 차(노랑)는 대환·명의 변경 뒤 옛 계약이 안 닫힌 것일 수 있으니 할부금 › 회사 실납부에서 확인해 주세요. 직영 차량(차주 「동영…」)은 공제 없이 회사가 내므로 「직영(회사 부담)」으로 따로 모았습니다.</CalcNote>
        <span className="ml-auto text-xs text-ink-soft">{mon} 공제 ↔ {data?.payMon ?? '다음 달'} 회사 납부 · 차이 합(회사 − 공제) {money(stat.all.amt)}</span>
      </div>
      <div className="flex flex-wrap gap-1 text-sm">
        {PICKS.map((k) => (
          <button key={k} className={pick === k ? 'btn-primary' : 'btn'} onClick={() => setPick(k)}>
            {LABEL[k]} {stat[k].n}{k !== 'same' && k !== 'all' && stat[k].amt ? ` · ${money(stat[k].amt)}` : ''}
          </button>
        ))}
      </div>
      {error && <ErrorBox message={error} />}
      {!data && !error && <div className="py-8 text-center text-sm text-ink-soft">세는 중…</div>}
      {data && (
        <div className="card max-h-[calc(100vh-14rem)] overflow-auto">
          <table className="w-full min-w-[56rem] text-sm tabular-nums">
            <thead className="sticky top-0 z-10 text-xs text-ink-soft [&_th]:bg-white">
              <tr className="border-b border-line">
                {['차량', '공제 차주', '납부 명의', '캐피탈', '법인'].map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}
                <th className="px-2 py-1.5 text-right" title="그 달 회사가 내는 살아 있는 계약 수">계약</th>
                <th className="px-2 py-1.5 text-right">기사 공제</th><th className="px-2 py-1.5 text-right">회사 납부</th><th className="px-2 py-1.5 text-right">차이(회사 − 공제)</th>
                <th className="px-2 py-1.5 text-right" title="기사 공제 중 회사할부 실납부에 연결된 공제 계약 몫 — 공제 합에 들어 있다">그중 회사할부 공제</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const gap = (n(r.pay) ?? 0) - (n(r.ded) ?? 0);
                return (
                  <tr key={r.car_no} className="border-b border-line/60 hover:bg-surface-soft">
                    <td className="whitespace-nowrap px-2 py-1"><a className="text-brand-700 hover:underline" href={`/fin-inst?q=${encodeURIComponent(r.car_no)}`}>{r.car_no}</a></td>
                    <td className="px-2 py-1">{r.owner_name ?? '—'}</td>
                    <td className="px-2 py-1 text-ink-soft">{r.holder_name ?? '—'}{r.unlinked && <span className="ml-1 rounded bg-slate-100 px-1 text-xs text-ink-soft" title="공제 계약과 연결 안 된 실납부(직영 등)">연결 없음</span>}</td>
                    <td className="px-2 py-1 text-ink-soft">{r.company ?? '—'}</td>
                    <td className="px-2 py-1 text-ink-soft">{r.corp_label ?? '—'}</td>
                    <td className={`px-2 py-1 text-right ${r.cnt > 1 ? 'font-medium text-amber-700' : 'text-ink-soft'}`} title={r.cnt > 1 ? '한 차에 계약 둘 이상 — 대환·명의 변경 뒤 안 닫힌 계약인지 확인' : ''}>{r.cnt || '—'}</td>
                    <td className="px-2 py-1 text-right">{r.ded === null ? '—' : money(Number(r.ded))}</td>
                    <td className="px-2 py-1 text-right">{r.pay === null ? '—' : money(Number(r.pay))}</td>
                    <td className={`px-2 py-1 text-right font-medium ${gap > 0 ? 'text-red-700' : gap < 0 ? 'text-green-700' : 'text-ink-faint'}`}>{money(gap)}</td>
                    <td className="px-2 py-1 text-right text-ink-soft">{r.loan === null ? '—' : money(Number(r.loan))}</td>
                  </tr>
                );
              })}
              {shown.length === 0 && <tr><td colSpan={10} className="py-6 text-center text-ink-soft">해당하는 차량이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
